// The first visible frame must be the hydrated server render, not a later rebuild.
//
// Vue defers the hydration of a defineAsyncComponent() whose code has not been
// resolved yet, keeping its server-rendered DOM. If any update reaches that
// component first, Vue skips the hydration and discards the server DOM, then
// mounts it again on the client once the code arrives. Inertia always produces
// such an update: the initial router.init() resolves the page again and calls
// swapComponent(), which re-renders every layout. Unresolved layouts and icons
// therefore made the page disappear and come back without icons after the
// initial render gate had revealed it. Resolve them before mounting.

export const INITIAL_ICONS_ATTRIBUTE = 'data-catalog-initial-icons';

/** Makes a defineAsyncComponent() wrapper render (and hydrate) synchronously. */
export function resolveAsyncComponent(component) {
    if (typeof component?.__asyncLoader !== 'function') return Promise.resolve(component);
    return component.__asyncLoader().then(() => component);
}

/**
 * Inertia layouts can be a component, a list of layouts, a { component, props }
 * pair or a callback that renders the tenant layout (workspace pages).
 */
export function resolveInitialLayouts(layout, tenantLayout) {
    if (!layout) return Promise.resolve();
    if (Array.isArray(layout)) return Promise.all(layout.map(entry => resolveInitialLayouts(entry, tenantLayout)));
    if (typeof layout?.__asyncLoader === 'function') return resolveAsyncComponent(layout);
    if (typeof layout === 'function') return resolveAsyncComponent(tenantLayout);
    if (typeof layout === 'object' && layout.component) return resolveInitialLayouts(layout.component, tenantLayout);
    return Promise.resolve(layout);
}

/** Icons rendered by SSR, as [name, theme, shared] requests reported by Icon.vue. */
export function readInitialIcons(document = globalThis.document) {
    const element = document?.querySelector?.(`script[${INITIAL_ICONS_ATTRIBUTE}]`);
    if (!element) return [];
    try {
        const icons = JSON.parse(element.textContent || '[]');
        return Array.isArray(icons)
            ? icons.filter(icon => Array.isArray(icon) && typeof icon[0] === 'string' && typeof icon[1] === 'string')
            : [];
    } catch {
        return [];
    }
}

/**
 * Icon requests of the packs built by scripts/vite-icon-packs.mjs: the active
 * theme, the Default theme it falls back to and the shared set. Resolving them
 * before mount lets every interface icon render synchronously, including those
 * that only appear after the data or a user action.
 */
export function iconPackRequests(theme, packs) {
    const names = group => (Array.isArray(packs?.themes?.[group]) ? packs.themes[group] : []);
    return [
        ...names(theme).map(name => [name, theme, 0]),
        ...(theme === 'Default' ? [] : names('Default').map(name => [name, 'Default', 0])),
        ...(Array.isArray(packs?.shared) ? packs.shared : []).map(name => [name, theme, 1]),
    ];
}

/**
 * Resolves the icon components of the server render. A failed icon chunk keeps
 * the previous lazy behaviour instead of blocking the application start.
 */
export function resolveInitialIcons(icons, resolveIcon) {
    const requested = new Set();
    return Promise.all(icons.map(([name, theme, shared]) => {
        const icon = resolveIcon(name, { theme, shared: Boolean(shared) });
        if (icon?.kind !== 'component' || requested.has(icon.value)) return null;
        requested.add(icon.value);
        return resolveAsyncComponent(icon.value).catch(() => null);
    }));
}

/** Server-side: the head element listing the icons rendered for this page. */
export function initialIconsHeadElement(icons) {
    const json = JSON.stringify([...icons].map(icon => JSON.parse(icon)))
        .replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')
        .replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
    return `<script type="application/json" ${INITIAL_ICONS_ATTRIBUTE}>${json}</script>`;
}

/**
 * Resolves after Inertia has committed the initial page (its first "navigate"
 * event follows the initial swapComponent()). The timeout keeps a failed or
 * unusual initial visit from holding the page hidden.
 */
export function waitForInitialNavigation(router, timeout = 1000) {
    return new Promise((resolve) => {
        let stop = () => {};
        const timer = setTimeout(() => { stop(); resolve(false); }, timeout);
        stop = router.on('navigate', () => {
            clearTimeout(timer);
            stop();
            resolve(true);
        }) ?? (() => {});
    });
}
